import { Checkbox, CheckboxProps, styled } from "@mui/material";

const focusVisibleOutline = {
	".Mui-focusVisible &": {
		outline: "2px solid var(--focus-outline-color-dark)",
		outlineOffset: "3px",
	},
};

const Icon = styled("span", {
	shouldForwardProp: (prop) => prop !== "disableFocusOutline" && prop !== "size",
})<{ size?: string | number; disableFocusOutline?: boolean }>(({ size, disableFocusOutline }) => ({
	width: size ?? "16px",
	height: size ?? "16px",
	border: "1px solid #6F6F6F",
	borderRadius: "2px",
	backgroundColor: "#fff",
	...(disableFocusOutline ? {} : { ...focusVisibleOutline }),
	"input:hover ~ &": {
		backgroundColor: "#e6e6e6",
	},
	"input:disabled ~ &": {
		borderColor: "#C4C4C4",
		backgroundColor: "#F5F5F5",
	},
}));

const CheckedIcon = styled(Icon, {
	shouldForwardProp: (prop) => prop !== "disableFocusOutline",
})<{ disableFocusOutline?: boolean }>(({ disableFocusOutline }) => ({
	border: "1px solid #0b6f85",
	borderRadius: "2px",
	backgroundColor: "#0b6f85",
	"&::before": {
		display: "block",
		width: "16px",
		height: "16px",
		backgroundImage:
			"url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 17 17'%3E%3Cpath" +
			" fill-rule='evenodd' clip-rule='evenodd' d='M13.5089 4.40867L6.49263 12.1259L2.98448 8.26746' " +
			"fill='%230000' stroke='%23fff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
		content: '""',
	},
	".Mui-disabled &::before": {
		backgroundImage:
			"url(\"data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='-1 -1 18 18'%3E%3Cpath" +
			" fill-rule='evenodd' clip-rule='evenodd' d='M13.5089 4.40867L6.49263 12.1259L2.98448 8.26746' " +
			"fill='%230000' stroke='%23948DA2' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E\")",
	},
	"input:disabled ~ &&": {
		borderColor: "#C4C4C4",
		backgroundColor: "#F5F5F5",
	},
	"input:hover ~ &": {
		backgroundColor: "#0a6478",
	},
	"input:active ~ &": {
		backgroundColor: "#09596a",
	},
	...(disableFocusOutline ? {} : { ...focusVisibleOutline }),
}));

const IndeterminateIcon = styled(Icon, {
	shouldForwardProp: (prop) => prop !== "disableFocusOutline",
})<{ disableFocusOutline?: boolean }>(({ disableFocusOutline }) => ({
	position: "relative",
	border: "1px solid #0b6f85",
	borderRadius: "2px",
	backgroundColor: "#0b6f85",
	"&::before": {
		content: '""',
		position: "absolute",
		top: "50%",
		left: "50%",
		width: "8px",
		height: "2px",
		backgroundColor: "#fff",
		transform: "translate(-50%, -50%)",
	},
	".Mui-disabled &::before": {
		backgroundColor: "#948DA2",
	},
	"input:hover ~ &": {
		backgroundColor: "#0a6478",
	},
	"input:active ~ &": {
		backgroundColor: "#09596a",
	},
	...(disableFocusOutline ? {} : { ...focusVisibleOutline }),
}));

export function CoreCheckbox({
	size,
	disableFocusOutline = false,
	...props
}: Omit<CheckboxProps, "size"> & { size?: string | number; disableFocusOutline?: boolean }) {
	return (
		<Checkbox
			{...props}
			icon={<Icon size={size} disableFocusOutline={disableFocusOutline} />}
			checkedIcon={<CheckedIcon size={size} disableFocusOutline={disableFocusOutline} />}
			indeterminateIcon={<IndeterminateIcon disableFocusOutline={disableFocusOutline} />}
			disableRipple
			size={undefined}
			sx={{
				...props.sx,
				padding: "4px",
				boxSizing: "content-box",
				marginLeft: props.edge === "start" ? "-6px" : undefined,
				"&.MuiCheckbox-indeterminate": {
					color: "#0B6F85",
				},
			}}
		/>
	);
}
